// This file contains the core components for the RAC form part of the DEL. This will also be used to create a stand alone RAC form as well.

import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { useAppStore } from "../../../store/appStore";
import rac_matrix from "../../../imgs/rac_matrix.webp";
import { racOptions, hazardTypeTooltip } from "../../../constants";
import styles from "./jsaRacInfoComponents.module.css";

// RAC Pictogram Modal to be used for users to reference when filling out the RAC form. This just contain the picture of the RAC help guide.
export const RACPictogramModal = ({ isOpen, onClose }) => {
  // Handle escape key press
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Handle click outside modal
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent}>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close modal"
        >
          ×
        </button>
        <img
          src={rac_matrix}
          alt="RAC Pictogram"
          className={styles.racPictogram}
        />
      </div>
    </div>
  );
};

// JSA RAC tool tip for the Hazard Type table cell
export const RacHazardTypeTooltip = ({ hazardType }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);
  const buttonRef = useRef(null);
  const containerRef = useRef(null);

  // Handle escape key press for tooltip
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === "Escape" && showTooltip) {
        setShowTooltip(false);
      }
    };

    const handleGlobalClick = (e) => {
      // Check if click is outside both the tooltip and the button
      if (
        showTooltip &&
        tooltipRef.current &&
        buttonRef.current &&
        !tooltipRef.current.contains(e.target) &&
        !buttonRef.current.contains(e.target)
      ) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener("keydown", handleEscapeKey);
      document.addEventListener("click", handleGlobalClick);

      // Adjust tooltip position if it goes off-screen
      if (tooltipRef.current && containerRef.current) {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        if (tooltipRect.right > viewportWidth) {
          tooltipRef.current.style.left = "auto";
          tooltipRef.current.style.right = "0";

          // Also move the arrow
          const arrow = tooltipRef.current.querySelector("::before");
          if (arrow) {
            arrow.style.left = "auto";
            arrow.style.right = "20px";
          }
        }
      }
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.removeEventListener("click", handleGlobalClick);
    };
  }, [showTooltip]);

  // Setting up a switch statement for the hazard type tooltip.
  const handleHazardTypeTooltip = (hazardType) => {
    return hazardTypeTooltip(hazardType);
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowTooltip(!showTooltip);
  };

  return (
    <div className={styles.hazardButtonContainer} ref={containerRef}>
      <button
        ref={buttonRef}
        className={styles.hazardTypeButton}
        onClick={handleButtonClick}
        aria-label={`Hazard type ${hazardType} information`}
        type="button"
      >
        {hazardType}
      </button>

      {showTooltip && (
        <div ref={tooltipRef} role="tooltip" className={styles.tooltip}>
          <button
            className={styles.tooltipCloseButton}
            onClick={() => setShowTooltip(false)}
            aria-label="Close tooltip"
          >
            ×
          </button>
          {handleHazardTypeTooltip(hazardType)}
        </div>
      )}
    </div>
  );
};

// Helper function to get field error for RAC Info fields
const getRacFieldError = (autosaveErrors, fieldName) => {
  const key = `jsa_rac_info.${fieldName}`;
  return autosaveErrors?.fieldErrors?.[key] || null;
};

export const JsaRacTableRow = ({ hazardType, callbacks }) => {
  const isTouch = useAppStore((s) => s.landingPage?.isTouch);
  const portalTarget =
    typeof document !== "undefined" ? document.body : undefined;

  // Get autosave errors for this row's fields
  const potHazError = getRacFieldError(callbacks.autosaveErrors, hazardType.fieldNames.potHazField);
  const elimHazError = getRacFieldError(callbacks.autosaveErrors, hazardType.fieldNames.elimHazField);
  // Custom styles for the Select dropdown
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.data.color,
      color: "white",
      fontWeight: "bold",
      ":hover": {
        backgroundColor: state.data.color,
        opacity: 0.9,
      },
    }),
    singleValue: (provided, state) => ({
      ...provided,
      backgroundColor: state.data.color,
      color: "white",
      fontWeight: "bold",
      padding: "2px 8px",
      borderRadius: "4px",
    }),
    control: (provided) => ({
      ...provided,
      minWidth: "85px",
    }),
  };
  const touchSelectProps = isTouch
    ? {
        menuPosition: "absolute",
        menuPlacement: "auto",
        menuShouldScrollIntoView: false,
        menuPortalTarget: portalTarget,
        styles: {
          menu: (base) => ({ ...base, zIndex: 9999 }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        },
      }
    : {
        menuPosition: "fixed",
        menuPortalTarget: portalTarget,
        styles: {
          menu: (base) => ({ ...base, zIndex: 9999 }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        },
      };

  // Handle select hazard code
  const handleSelectHazardCode = (selectedOption) => {
    hazardType.functions.codeFunc(callbacks.jsaData, selectedOption.value, callbacks.emitPatch);
    // Commit change for autosave (onChange for selects)
    try {
      callbacks?.commit?.rac?.(
        callbacks.jsaData,
        hazardType.fieldNames.codeField,
        selectedOption.value
      );
    } catch {}
  };

  // Handle show hazard code value
  const handleShowHazardCodeValue = () => {
    return racOptions.find(
      (option) => option.value === hazardType.callbackValues.codeValue
    );
  };

  // Handle update potential hazards info (local state only on onChange)
  const handleUpdatePotentialHazardsInfo = (e) => {
    hazardType.functions.potHazFunc(callbacks.jsaData, e.target.value, null); // Don't emit on every keystroke
  };

  // Handle blur potential hazards info - send patch to server
  const handleBlurPotentialHazardsInfo = (e) => {
    hazardType.functions.potHazFunc(callbacks.jsaData, e.target.value, callbacks.emitPatch); // Emit on blur
    try {
      callbacks?.commit?.rac?.(
        callbacks.jsaData,
        hazardType.fieldNames.potHazField,
        hazardType.callbackValues.potHazValue
      );
    } catch {}
  };

  // Handle update hazard elimination info (local state only on onChange)
  const handleUpdateHazardEliminationInfo = (e) => {
    hazardType.functions.elimHazFunc(callbacks.jsaData, e.target.value, null); // Don't emit on every keystroke
  };

  // Handle blur hazard elimination info - send patch to server
  const handleBlurHazardEliminationInfo = (e) => {
    hazardType.functions.elimHazFunc(callbacks.jsaData, e.target.value, callbacks.emitPatch); // Emit on blur
    try {
      callbacks?.commit?.rac?.(
        callbacks.jsaData,
        hazardType.fieldNames.elimHazField,
        hazardType.callbackValues.elimHazValue
      );
    } catch {}
  };

  return (
    <div className={styles.jsaRacRow}>
      {/* First row with Hazard Type and RAC Code */}
      <div className={styles.jsaRacHeaderRow}>
        <div className={styles.jsaRacHazardType}>
          <label>Hazard:</label>
          <RacHazardTypeTooltip hazardType={hazardType.value} />
        </div>
        <div className={styles.jsaRacCode}>
          <label>RAC Code:</label>
          <Select
            options={racOptions}
            value={handleShowHazardCodeValue()}
            onChange={handleSelectHazardCode}
            className={styles.jsaRacSelect}
            styles={{ ...customStyles, ...(touchSelectProps.styles || {}) }}
            menuPortalTarget={touchSelectProps.menuPortalTarget}
            menuPosition={touchSelectProps.menuPosition}
            menuPlacement={touchSelectProps.menuPlacement}
            menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
            isDisabled={callbacks.jsaMode === "view"}
          />
        </div>
      </div>

      {/* Second row with Potential Hazards and Hazard Elimination */}
      <div className={styles.jsaRacContentRow}>
        <div className={styles.jsaRacInput}>
          <label>Potential Hazards:</label>
          <textarea
            value={hazardType.callbackValues.potHazValue || ""}
            onChange={handleUpdatePotentialHazardsInfo}
            onBlur={handleBlurPotentialHazardsInfo}
            className={`${styles.jsaRacTextarea} ${potHazError ? styles.textareaError : ""}`}
            disabled={callbacks.jsaMode === "view"}
          />
          {potHazError && <span className={styles.errorText}>{potHazError}</span>}
        </div>
        <div className={styles.jsaRacInput}>
          <label>Hazard Elimination:</label>
          <textarea
            value={hazardType.callbackValues.elimHazValue || ""}
            onChange={handleUpdateHazardEliminationInfo}
            onBlur={handleBlurHazardEliminationInfo}
            className={`${styles.jsaRacTextarea} ${elimHazError ? styles.textareaError : ""}`}
            disabled={callbacks.jsaMode === "view"}
          />
          {elimHazError && <span className={styles.errorText}>{elimHazError}</span>}
        </div>
      </div>
    </div>
  );
};

export const JsaRacTable = ({ callbacks }) => {
  const RAC_TABLE_MAP = {
    ACCESS: {
      label: "Access",
      value: "Access",
      functions: {
        potHazFunc: callbacks.coreActions.updateJsaRacAccessPotHazInfo,
        elimHazFunc: callbacks.coreActions.updateJsaRacAccessElimHazInfo,
        codeFunc: callbacks.coreActions.updateJsaRacAccessCode,
      },
      callbackValues: {
        potHazValue: callbacks.jsaData.jsaRacInfo.racAccessPotHazInfo,
        elimHazValue: callbacks.jsaData.jsaRacInfo.racAccessElimHazInfo,
        codeValue: callbacks.jsaData.jsaRacInfo.racAccessCode,
      },
      fieldNames: {
        potHazField: "rac_access_pot_haz_info",
        elimHazField: "rac_access_elim_haz_info",
        codeField: "rac_access_code",
      },
    },
    CHEMICAL: {
      label: "Chemicals/Materials",
      value: "Chemical",
      functions: {
        potHazFunc: callbacks.coreActions.updateJsaRacChemicalPotHazInfo,
        elimHazFunc: callbacks.coreActions.updateJsaRacChemicalElimHazInfo,
        codeFunc: callbacks.coreActions.updateJsaRacChemicalCode,
      },
      callbackValues: {
        potHazValue: callbacks.jsaData.jsaRacInfo.racChemicalPotHazInfo,
        elimHazValue: callbacks.jsaData.jsaRacInfo.racChemicalElimHazInfo,
        codeValue: callbacks.jsaData.jsaRacInfo.racChemicalCode,
      },
      fieldNames: {
        potHazField: "rac_chemical_pot_haz_info",
        elimHazField: "rac_chemical_elim_haz_info",
        codeField: "rac_chemical_code",
      },
    },
    ENVIRONMENT: {
      label: "Environmental",
      value: "Environment",
      functions: {
        potHazFunc: callbacks.coreActions.updateJsaRacEnvironmentPotHazInfo,
        elimHazFunc: callbacks.coreActions.updateJsaRacEnvironmentElimHazInfo,
        codeFunc: callbacks.coreActions.updateJsaRacEnvironmentCode,
      },
      callbackValues: {
        potHazValue: callbacks.jsaData.jsaRacInfo.racEnvironmentPotHazInfo,
        elimHazValue: callbacks.jsaData.jsaRacInfo.racEnvironmentElimHazInfo,
        codeValue: callbacks.jsaData.jsaRacInfo.racEnvironmentCode,
      },
      fieldNames: {
        potHazField: "rac_environment_pot_haz_info",
        elimHazField: "rac_environment_elim_haz_info",
        codeField: "rac_environment_code",
      },
    },
    PHYSICAL: {
      label: "Physical",
      value: "Physical",
      functions: {
        potHazFunc: callbacks.coreActions.updateJsaRacPhysicalPotHazInfo,
        elimHazFunc: callbacks.coreActions.updateJsaRacPhysicalElimHazInfo,
        codeFunc: callbacks.coreActions.updateJsaRacPhysicalCode,
      },
      callbackValues: {
        potHazValue: callbacks.jsaData.jsaRacInfo.racPhysicalPotHazInfo,
        elimHazValue: callbacks.jsaData.jsaRacInfo.racPhysicalElimHazInfo,
        codeValue: callbacks.jsaData.jsaRacInfo.racPhysicalCode,
      },
      fieldNames: {
        potHazField: "rac_physical_pot_haz_info",
        elimHazField: "rac_physical_elim_haz_info",
        codeField: "rac_physical_code",
      },
    },
    POT_ENERGY: {
      label: "Potential Energy",
      value: "Potential Energy",
      functions: {
        potHazFunc: callbacks.coreActions.updateJsaRacPotEnergyPotHazInfo,
        elimHazFunc: callbacks.coreActions.updateJsaRacPotEnergyElimHazInfo,
        codeFunc: callbacks.coreActions.updateJsaRacPotEnergyCode,
      },
      callbackValues: {
        potHazValue: callbacks.jsaData.jsaRacInfo.racPotEnergyPotHazInfo,
        elimHazValue: callbacks.jsaData.jsaRacInfo.racPotEnergyElimHazInfo,
        codeValue: callbacks.jsaData.jsaRacInfo.racPotEnergyCode,
      },
      fieldNames: {
        potHazField: "rac_pot_energy_pot_haz_info",
        elimHazField: "rac_pot_energy_elim_haz_info",
        codeField: "rac_pot_energy_code",
      },
    },
    TOOLS: {
      label: "Tools/Equipment",
      value: "Tools",
      functions: {
        potHazFunc: callbacks.coreActions.updateJsaRacToolsPotHazInfo,
        elimHazFunc: callbacks.coreActions.updateJsaRacToolsElimHazInfo,
        codeFunc: callbacks.coreActions.updateJsaRacToolsCode,
      },
      callbackValues: {
        potHazValue: callbacks.jsaData.jsaRacInfo.racToolsPotHazInfo,
        elimHazValue: callbacks.jsaData.jsaRacInfo.racToolsElimHazInfo,
        codeValue: callbacks.jsaData.jsaRacInfo.racToolsCode,
      },
      fieldNames: {
        potHazField: "rac_tools_pot_haz_info",
        elimHazField: "rac_tools_elim_haz_info",
        codeField: "rac_tools_code",
      },
    },
    OTHER: {
      label: "Other",
      value: "Other",
      functions: {
        potHazFunc: callbacks.coreActions.updateJsaRacOtherPotHazInfo,
        elimHazFunc: callbacks.coreActions.updateJsaRacOtherElimHazInfo,
        codeFunc: callbacks.coreActions.updateJsaRacOtherCode,
      },
      callbackValues: {
        potHazValue: callbacks.jsaData.jsaRacInfo.racOtherPotHazInfo,
        elimHazValue: callbacks.jsaData.jsaRacInfo.racOtherElimHazInfo,
        codeValue: callbacks.jsaData.jsaRacInfo.racOtherCode,
      },
      fieldNames: {
        potHazField: "rac_other_pot_haz_info",
        elimHazField: "rac_other_elim_haz_info",
        codeField: "rac_other_code",
      },
    },
  };

  return (
    <div className={styles.jsaRacTableContainer}>
      <JsaRacTableRow callbacks={callbacks} hazardType={RAC_TABLE_MAP.ACCESS} />
      <JsaRacTableRow
        callbacks={callbacks}
        hazardType={RAC_TABLE_MAP.CHEMICAL}
      />
      <JsaRacTableRow
        callbacks={callbacks}
        hazardType={RAC_TABLE_MAP.ENVIRONMENT}
      />
      <JsaRacTableRow
        callbacks={callbacks}
        hazardType={RAC_TABLE_MAP.PHYSICAL}
      />
      <JsaRacTableRow
        callbacks={callbacks}
        hazardType={RAC_TABLE_MAP.POT_ENERGY}
      />
      <JsaRacTableRow callbacks={callbacks} hazardType={RAC_TABLE_MAP.TOOLS} />
      <JsaRacTableRow callbacks={callbacks} hazardType={RAC_TABLE_MAP.OTHER} />
    </div>
  );
};

// JSA RAC Info Component
export const JsaRacInfoTab = ({ callbacks }) => {
  const [isRacModalOpen, setIsRacModalOpen] = useState(false);
  return (
    <div className={styles.jsaRacInfoContainer}>
      <div className={styles.jsaRacInfoHelperRow}>
        {/* Helper Button- Shows the RAC Modal */}
        <button
          className={styles.jsaRacHelperButton}
          onClick={() => setIsRacModalOpen(true)}
        >
          RAC Helper
        </button>
        {/* RAC Pictogram Modal */}
        <RACPictogramModal
          isOpen={isRacModalOpen}
          onClose={() => setIsRacModalOpen(false)}
        />
      </div>
      {/* RAC Tables */}
      <div className={styles.jsaRacTableWrapper}>
        <JsaRacTable callbacks={callbacks} />
      </div>
    </div>
  );
};
